// src/judge/useShotTimer.ts
//
// Časomíra střelce na gongech (fáze 3, G1) – jedna pro JudgeGongsSequential
// i JudgeGongsChoice (dřív dvě kopie, „Pokračovat“ po pauze časomíru
// nerozběhlo a odpočet „čekat → start“ ani hlášky nešly zrušit).
//
// - Podle koncového času (endAt), ne počítáním tiků: vytížený starý tablet
//   nebo skrytá stránka tik zpozdí, ale čas neujede.
// - Pauza si uloží zbývající čas, pokračování nastaví nový konec.
// - Hlášky (audio.timerAnnounce) při překročení prahu; po dohnání (víc
//   sekund naráz) jen poslední práh se skutečným zbývajícím časem
//   (docs/live-faze3-navrh.md bod 5.5).
// - Pauza, reset i odchod (další střelec má vlastní gongy) ruší interval,
//   odpočet „čekat → start“ i naplánované hlášky – nikdy
//   speechSynthesis.cancel().
// - Beze změny: „Čekat → Start“ 3 s, hlas „čekat“ / „start“, pípnutí 880 Hz.

import { useCallback, useEffect, useRef, useState } from "react";
import { cancelTimerAnnouncements, isTimerThreshold, playBeep, speak, timerAnnounce } from "../utils/audio";

/** Jak často se kontroluje zbývající čas (zobrazení i hlášky se mění po sekundách). */
const TICK_MS = 250;
/** Odpočet „čekat → start“. */
export const AUTO_START_DELAY_MS = 3000;

export type ShotTimerPhase = "idle" | "countdown" | "running" | "paused";

export interface ShotTimer {
  /** Zobrazené sekundy (zbývající, zaokrouhlené nahoru). */
  secs: number;
  phase: ShotTimerPhase;
  /** Ruční start. */
  start: () => void;
  /** „Čekat → Start“: hlas „čekat“, za 3 s „start“, pípnutí a start. */
  startAuto: () => void;
  pause: () => void;
  resume: () => void;
  /** Zpět na začátek (bez zvuku). */
  reset: () => void;
}

export function useShotTimer(timeLimit: number | null | undefined, soundEnabled: boolean): ShotTimer {
  const limit = timeLimit && timeLimit > 0 ? timeLimit : 0;
  const [secs, setSecs] = useState(limit);
  const [phase, setPhase] = useState<ShotTimerPhase>("idle");

  const endAtRef = useRef(0);
  const remainingRef = useRef(limit * 1000);
  const lastSecsRef = useRef(limit);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const soundRef = useRef(soundEnabled);
  soundRef.current = soundEnabled;

  const stopInterval = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
  };
  const stopAuto = () => {
    if (autoRef.current) clearTimeout(autoRef.current);
    autoRef.current = null;
  };

  const tick = useCallback(() => {
    const remaining = Math.max(0, endAtRef.current - Date.now());
    const now = Math.ceil(remaining / 1000);
    const prev = lastSecsRef.current;
    if (now < prev) {
      lastSecsRef.current = now;
      setSecs(now);
      if (soundRef.current) {
        // Překročené sekundy prev-1 … now: ohlásí se jen poslední práh
        for (let s = now; s < prev; s++) {
          if (isTimerThreshold(s, limit)) {
            timerAnnounce(s, limit, remaining);
            break;
          }
        }
      }
    }
    if (remaining <= 0) {
      stopInterval();
      setPhase("idle");
    }
  }, [limit]);

  const run = useCallback((remainingMs: number) => {
    stopInterval();
    endAtRef.current = Date.now() + remainingMs;
    setPhase("running");
    intervalRef.current = setInterval(tick, TICK_MS);
  }, [tick]);

  const start = useCallback(() => {
    if (!limit) return;
    stopAuto();
    cancelTimerAnnouncements();
    lastSecsRef.current = limit;
    setSecs(limit);
    run(limit * 1000);
  }, [limit, run]);

  const startAuto = useCallback(() => {
    if (!limit) return;
    stopInterval();
    stopAuto();
    cancelTimerAnnouncements();
    lastSecsRef.current = limit;
    setSecs(limit);
    setPhase("countdown");
    speak("čekat");
    autoRef.current = setTimeout(() => {
      autoRef.current = null;
      speak("start");
      playBeep(880, 0.6, 0.3);
      run(limit * 1000);
    }, AUTO_START_DELAY_MS);
  }, [limit, run]);

  const pause = useCallback(() => {
    if (!intervalRef.current) return;
    stopInterval();
    remainingRef.current = Math.max(0, endAtRef.current - Date.now());
    cancelTimerAnnouncements();
    setPhase("paused");
  }, []);

  const resume = useCallback(() => {
    const remaining = remainingRef.current;
    run(remaining);
    // Pauza zrušila naplánovaný odpočet – naplánovat znovu podle zbytku
    if (soundRef.current && remaining <= 6000 && isTimerThreshold(6, limit)) timerAnnounce(6, limit, remaining, { resumed: true });
  }, [run, limit]);

  const reset = useCallback(() => {
    stopInterval();
    stopAuto();
    cancelTimerAnnouncements();
    lastSecsRef.current = limit;
    remainingRef.current = limit * 1000;
    setSecs(limit);
    setPhase("idle");
  }, [limit]);

  // Jiný časový limit = nová časomíra; odchod (další střelec má vlastní
  // gongy) zruší interval, odpočet i hlášky
  useEffect(() => {
    reset();
    return () => {
      stopInterval();
      stopAuto();
      cancelTimerAnnouncements();
    };
  }, [reset]);

  return { secs, phase, start, startAuto, pause, resume, reset };
}
